import React, { useState } from 'react';
import {
  Box, Typography, IconButton, TextField, Tooltip, LinearProgress, Button,
} from '@mui/material';
import {
  Spa as SpaIcon,
  Face as FaceIcon,
  MonitorHeart as TorsoIcon,
  Adjust as AbdomenIcon,
  Wc as WcIcon,
  AccessibilityNew as ExtremidadesIcon,
  Psychology as NeuroIcon,
  NoteAlt as NotesIcon,
  AutoFixHigh as AutoFixIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  RestartAlt as RestartIcon,
} from '@mui/icons-material';

const SECCIONES = [
  {
    key: 'piel_faneras',
    label: 'Piel y faneras',
    icono: <SpaIcon />,
    color: '#f97316',
    plantilla: 'Piel íntegra, de color, temperatura, turgencia y elasticidad normales. Sin lesiones primarias ni secundarias, cicatrices, máculas, pápulas ni exantemas. Mucosas bien hidratadas. Cabello de implantación y distribución normal. Uñas de color, forma y grosor normales sin alteraciones.',
  },
  {
    key: 'cabeza_cuello',
    label: 'Cabeza y cuello',
    icono: <FaceIcon />,
    color: '#ec4899',
    plantilla: 'Normocéfalo, sin deformidades. Pupilas isocóricas, reactivas a la luz. Conjuntivas normales, escleras blancas. Fosas nasales permeables. Orofaringe sin lesiones, amígdalas simétricas. Cuello cilíndrico, móvil, sin adenopatías, tráquea central, tiroides no palpable.',
  },
  {
    key: 'torax',
    label: 'Tórax',
    icono: <TorsoIcon />,
    color: '#0ea5e9',
    plantilla: 'Tórax simétrico, expansibilidad conservada. Ruidos cardíacos rítmicos, sin soplos ni ruidos agregados, frecuencia regular. Murmullo vesicular conservado en ambos hemitórax, sin estertores ni sibilancias.',
  },
  {
    key: 'abdomen',
    label: 'Abdomen',
    icono: <AbdomenIcon />,
    color: '#f59e0b',
    plantilla: 'Abdomen plano, blando, depresible, no doloroso a la palpación superficial ni profunda. Ruidos hidroaéreos presentes de características normales. Sin visceromegalias ni masas palpables. Murphy, McBurney y Blumberg negativos.',
  },
  {
    key: 'genitales',
    label: 'Genitales',
    icono: <WcIcon />,
    color: '#14b8a6',
    plantilla: 'Genitales externos de características normales para el género y edad. Sin lesiones visibles ni secreciones patológicas. Región inguinal sin hernias ni adenopatías palpables. Sin dolor a la exploración.',
  },
  {
    key: 'extremidades',
    label: 'Extremidades',
    icono: <ExtremidadesIcon />,
    color: '#10b981',
    plantilla: 'Extremidades simétricas, tono y trofismo conservados. Pulsos periféricos presentes y de adecuada amplitud. Llenado capilar menor a 2 segundos. Sin edemas, várices ni deformidades articulares. Arcos de movimiento completos.',
  },
  {
    key: 'neurologico',
    label: 'Neurológico',
    icono: <NeuroIcon />,
    color: '#8b5cf6',
    plantilla: 'Paciente consciente, orientado en tiempo, lugar y persona. Pares craneales sin alteraciones. Fuerza muscular 5/5 en las cuatro extremidades. Reflejos osteotendinosos ++. Sensibilidad conservada en todas las modalidades. Sin signos meníngeos. Marcha estable.',
  },
  {
    key: 'observaciones',
    label: 'Observaciones',
    icono: <NotesIcon />,
    color: '#64748b',
    plantilla: '',
    opcional: true,
  },
];

export default function ExploracionFisica({ data = {}, onChange }) {
  const [seccionActiva, setSeccionActiva] = useState(0);

  const updateSeccion = (key, value) => {
    onChange({ ...data, [key]: value });
  };

  const aplicarPlantilla = (seccion) => {
    if (seccion.plantilla) updateSeccion(seccion.key, seccion.plantilla);
  };

  const limpiar = (key) => updateSeccion(key, '');

  const seccionesObligatorias = SECCIONES.filter(s => !s.opcional);
  const completadas = seccionesObligatorias.filter(s => (data[s.key] || '').trim().length > 0).length;
  const progreso = (completadas / seccionesObligatorias.length) * 100;

  const seccion = SECCIONES[seccionActiva];
  const valorActual = data[seccion.key] || '';
  const seccionLlena = valorActual.trim().length > 0;

  return (
    <Box>
      {/* Barra de progreso */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            {completadas} de {seccionesObligatorias.length} secciones
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: progreso === 100 ? '#10b981' : '#64748b' }}>
            {Math.round(progreso)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progreso}
          sx={{
            height: 4, borderRadius: 2, background: '#e2e8f0',
            '& .MuiLinearProgress-bar': {
              background: progreso === 100
                ? 'linear-gradient(90deg, #10b981, #059669)'
                : 'linear-gradient(90deg, #8b5cf6, #6366f1)',
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* Layout master-detail */}
      <Box sx={{ display: 'flex', gap: 0, minHeight: 340, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff' }}>

        {/* Sidebar */}
        <Box sx={{
          width: { xs: 48, sm: 170 }, flexShrink: 0,
          background: '#f8fafc', borderRight: '1px solid #e2e8f0',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
        }}>
          {SECCIONES.map((s, idx) => {
            const activa = idx === seccionActiva;
            const llena = (data[s.key] || '').trim().length > 0;
            return (
              <Box
                key={s.key}
                onClick={() => setSeccionActiva(idx)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: { xs: 0.8, sm: 1.5 }, py: 1.3,
                  cursor: 'pointer', position: 'relative',
                  transition: 'all 0.2s',
                  background: activa ? '#fff' : 'transparent',
                  borderBottom: '1px solid #f1f5f9',
                  '&:hover': { background: activa ? '#fff' : '#f1f5f9' },
                  '&::before': activa ? {
                    content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: 3, borderRadius: '0 3px 3px 0',
                    background: s.color,
                  } : {},
                }}
              >
                <Box sx={{
                  width: 30, height: 30, borderRadius: 2, flexShrink: 0,
                  background: activa ? s.color : llena ? `${s.color}20` : '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                  boxShadow: activa ? `0 3px 10px ${s.color}40` : 'none',
                }}>
                  {React.cloneElement(s.icono, { sx: { fontSize: 16, color: activa ? '#fff' : llena ? s.color : '#94a3b8' } })}
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 11, fontWeight: activa ? 700 : 500, lineHeight: 1.2,
                    color: activa ? '#1e293b' : '#64748b',
                  }} noWrap>
                    {s.label}
                  </Typography>
                  {llena ? (
                    <CheckIcon sx={{ fontSize: 13, color: '#10b981', flexShrink: 0 }} />
                  ) : !s.opcional ? (
                    <EmptyIcon sx={{ fontSize: 13, color: '#cbd5e1', flexShrink: 0 }} />
                  ) : null}
                </Box>
              </Box>
            );
          })}

          {/* Botón "Todo normal" al final del sidebar */}
          <Box sx={{ p: 1, mt: 'auto', borderTop: '1px solid #e2e8f0' }}>
            <Tooltip title="Insertar 'Normal' en todas las secciones vacías">
              <Button
                size="small" fullWidth
                startIcon={<AutoFixIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  const nuevo = { ...data };
                  SECCIONES.filter(s => !s.opcional && s.plantilla && !(data[s.key] || '').trim()).forEach(s => {
                    nuevo[s.key] = s.plantilla;
                  });
                  onChange(nuevo);
                }}
                sx={{
                  textTransform: 'none', fontWeight: 600,
                  fontSize: { xs: 0, sm: 10 },
                  color: '#6366f1', borderRadius: 2,
                  border: '1px dashed #6366f1',
                  minWidth: 0, px: { xs: 0.5, sm: 1 },
                  '&:hover': { background: '#eef2ff' },
                }}
              >
                <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Todo normal</Box>
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Panel derecho: editor */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2.5 } }}>
          {/* Header de la sección */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                background: seccion.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 12px ${seccion.color}40`,
              }}>
                {React.cloneElement(seccion.icono, { sx: { fontSize: 18 } })}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: seccion.color, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Sección {seccionActiva + 1} de {SECCIONES.length}
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                  {seccion.label}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {seccion.plantilla && (
                <Tooltip title="Insertar plantilla 'dentro de parámetros normales'">
                  <Button
                    size="small"
                    startIcon={<AutoFixIcon sx={{ fontSize: 14 }} />}
                    onClick={() => aplicarPlantilla(seccion)}
                    sx={{
                      textTransform: 'none', fontWeight: 700, fontSize: 11,
                      color: seccion.color,
                      border: `1.5px solid ${seccion.color}`,
                      borderRadius: 2, px: 1.2,
                      '&:hover': { background: seccion.color, color: '#fff' },
                    }}
                  >
                    Normal
                  </Button>
                </Tooltip>
              )}
              {seccionLlena && (
                <Tooltip title="Limpiar">
                  <IconButton size="small" onClick={() => limpiar(seccion.key)} sx={{ color: '#94a3b8' }}>
                    <RestartIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Textarea */}
          <TextField
            fullWidth multiline
            minRows={8} maxRows={14}
            placeholder={seccion.opcional
              ? 'Anota cualquier hallazgo relevante no incluido en las secciones anteriores...'
              : `Describe los hallazgos de ${seccion.label.toLowerCase()}...`}
            value={valorActual}
            onChange={(e) => updateSeccion(seccion.key, e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2, background: '#fff', fontSize: 13, lineHeight: 1.6,
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: seccion.color },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: seccion.color, borderWidth: 2 },
              },
            }}
          />

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
              {valorActual.length > 0 ? `${valorActual.length} caracteres` : 'Sin documentar'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
